import { useState, useMemo, useEffect } from "react";
import { useLocation, Link, useSearchParams } from "react-router-dom";
import { api, SERVICES } from "@/lib/rn3";
import { toast } from "sonner";
import { Check, ArrowRight, FileText } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useT } from "@/lib/i18n";
import SEO from "@/components/SEO";

const SERVICE_OPTIONS = [
  { key: "general", title: "General Client Intake" },
  ...SERVICES,
];

const SERVICE_EXTRA_FIELDS = {
  bookkeeping: [
    { key: "business_name", labelKey: "intake.extra.business_name" },
    { key: "months_behind", labelKey: "intake.extra.months_behind" },
  ],
  tax_prep: [
    { key: "filing_status", labelKey: "intake.extra.filing_status" },
    { key: "dependents", labelKey: "intake.extra.dependents" },
  ],
  sales_tax: [
    { key: "state", labelKey: "intake.extra.state" },
    { key: "frequency", labelKey: "intake.extra.frequency" },
  ],
  notary: [
    { key: "doc_type", labelKey: "intake.extra.doc_type" },
    { key: "location", labelKey: "intake.extra.location" },
  ],
  loan_signing: [
    { key: "lender", labelKey: "intake.extra.lender" },
    { key: "signing_date", labelKey: "intake.extra.signing_date" },
  ],
  itin: [
    { key: "applicant_type", labelKey: "intake.extra.applicant_type" },
    { key: "country", labelKey: "intake.extra.country" },
  ],
  translation: [
    { key: "source_lang", labelKey: "intake.extra.source_lang" },
    { key: "target_lang", labelKey: "intake.extra.target_lang" },
  ],
  general: [],
};

const Intake = () => {
  const tt = useT();
  const loc = useLocation();
  const params = new URLSearchParams(loc.search);
  const preset = params.get("service") || "general";
  const { user } = useAuth();

  const [form, setForm] = useState({
    service: preset,
    full_name: user?.full_name || "",
    email: user?.email || "",
    phone: user?.phone || "",
    address: "",
    preferred_language: "en",
    urgency: "normal",
    description: "",
    consent: false,
    extra: {},
  });
  const [submitted, setSubmitted] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchParams] = useSearchParams();

  // Capture ?service=<key>&src=<source>&src_detail=<campaign> from QR or marketing links.
  // The `service` param pre-selects the matching service tab so a scan of, say,
  // the "Bookkeeping" QR lands the visitor on the Bookkeeping intake immediately.
  useEffect(() => {
    const validKeys = SERVICE_OPTIONS.map((s) => s.key);
    const svc = searchParams.get("service");
    const src = searchParams.get("src");
    const detail = searchParams.get("src_detail");
    setForm((f) => {
      const next = { ...f };
      if (svc && validKeys.includes(svc)) {
        next.service = svc;
        next.extra = {}; // reset per-service extras when switching via deep link
      }
      if (src) {
        next.extra = { ...(next.extra || {}), src, src_detail: detail || "" };
      }
      return next;
    });
    // eslint-disable-next-line
  }, []);

  const extraFields = useMemo(() => SERVICE_EXTRA_FIELDS[form.service] || [], [form.service]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error(tt("intake.toast_consent")); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/intakes", form);
      setSubmitted(data);
      toast.success(tt("intake.toast_ok"));
    } catch (err) {
      toast.error(err.response?.data?.detail || tt("intake.toast_fail"));
    } finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <div className="bg-[#FDFBF7] min-h-[60vh]" data-testid="intake-confirmation">
        <div className="rn3-container py-24 text-center">
          <Check className="w-12 h-12 text-[#C4A252] mx-auto" />
          <h1 className="font-serif-display text-4xl mt-4">{tt("intake.confirm.thanks")}, {submitted.full_name.split(" ")[0]}!</h1>
          <div className="border-t border-[#C4A252] w-12 mx-auto my-5" />
          <p className="text-[#404040] max-w-md mx-auto">
            {tt("intake.confirm.body_a")} <b>{submitted.service.replace(/_/g, " ")}</b> {tt("intake.confirm.body_b")}
          </p>

          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to={`/appointments?service=${submitted.service}`} className="rn3-btn-primary" data-testid="post-intake-schedule">
              {tt("intake.confirm.schedule")} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/portal" className="rn3-btn-gold" data-testid="post-intake-portal">{tt("intake.confirm.open_portal")}</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <SEO
        title="New Client Intake — Roman Numerals III | Jacksonville, FL"
        description="Get started with Roman Numerals III. Submit your bilingual client intake for bookkeeping, tax preparation, ITIN, notary, or sales tax services."
        path="/intake"
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "New Client Intake", path: "/intake" }]}
      />
      <section className="bg-[#FDFBF7]">
        <div className="rn3-container py-16">
          <div className="rn3-eyebrow">{tt("intake.eyebrow")}</div>
          <h1 className="font-serif-display text-5xl mt-4">
            {tt("intake.h1")}
          </h1>
          <p className="mt-4 max-w-xl text-[#404040]">{tt("intake.intro")}</p>
        </div>
      </section>

      <section className="rn3-section">
        <div className="rn3-container max-w-3xl">
          {/* service chips */}
          <div className="flex flex-wrap gap-2 mb-10" data-testid="intake-service-tabs">
            {SERVICE_OPTIONS.map((s) => (
              <button
                key={s.key}
                onClick={() => setForm({ ...form, service: s.key, extra: {} })}
                type="button"
                data-testid={`intake-tab-${s.key}`}
                className={`px-4 py-2 text-xs uppercase tracking-widest border transition-colors ${
                  form.service === s.key
                    ? "bg-[#8B1C22] text-white border-[#8B1C22]"
                    : "border-[#E5E5E5] hover:border-[#C4A252]"
                }`}
              >
                {s.key === "general" ? tt("intake.service.general") : tt(`service.${s.key}`, s.title)}
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="space-y-6" data-testid="intake-form">
            <div className="grid sm:grid-cols-2 gap-6">
              <div>
                <label className="rn3-eyebrow block mb-2">{tt("intake.full_name")}</label>
                <input required value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="rn3-input" data-testid="intake-name" />
              </div>
              <div>
                <label className="rn3-eyebrow block mb-2">{tt("intake.phone")}</label>
                <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="rn3-input" data-testid="intake-phone" />
              </div>
              <div>
                <label className="rn3-eyebrow block mb-2">{tt("intake.email")}</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rn3-input" data-testid="intake-email" />
              </div>
              <div>
                <label className="rn3-eyebrow block mb-2">{tt("intake.lang_label")}</label>
                <select value={form.preferred_language} onChange={(e) => setForm({ ...form, preferred_language: e.target.value })} className="rn3-input" data-testid="intake-lang">
                  <option value="en">{tt("common.english")}</option>
                  <option value="es">{tt("common.spanish")}</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="rn3-eyebrow block mb-2">{tt("intake.address")}</label>
                <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="rn3-input" data-testid="intake-address" />
              </div>
              <div>
                <label className="rn3-eyebrow block mb-2">{tt("intake.urgency")}</label>
                <select value={form.urgency} onChange={(e) => setForm({ ...form, urgency: e.target.value })} className="rn3-input" data-testid="intake-urgency">
                  <option value="low">{tt("intake.urgency.low")}</option>
                  <option value="normal">{tt("intake.urgency.normal")}</option>
                  <option value="high">{tt("intake.urgency.high")}</option>
                </select>
              </div>
              {extraFields.map((f) => (
                <div key={f.key}>
                  <label className="rn3-eyebrow block mb-2">{tt(f.labelKey)}</label>
                  <input
                    value={form.extra?.[f.key] || ""}
                    onChange={(e) => setForm({ ...form, extra: { ...form.extra, [f.key]: e.target.value } })}
                    className="rn3-input"
                    data-testid={`intake-extra-${f.key}`}
                  />
                </div>
              ))}
              <div className="sm:col-span-2">
                <label className="rn3-eyebrow block mb-2">{tt("intake.description")}</label>
                <textarea required value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rn3-input" rows="4" data-testid="intake-description" />
              </div>
            </div>

            <label className="flex items-start gap-3 text-sm text-[#404040]">
              <input type="checkbox" required checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} data-testid="intake-consent" className="mt-1" />
              <span>{tt("intake.consent_full")}</span>
            </label>

            <button type="submit" disabled={submitting} className="rn3-btn-primary" data-testid="intake-submit">
              <FileText className="w-4 h-4" /> {submitting ? tt("intake.submitting") : tt("intake.submit")}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Intake;
